import { Injectable } from '@angular/core';
import { LogService } from '../log/log.service';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  private _storage: Storage = localStorage;

  constructor() { }

  setItem(key: string, value: any) {
    LogService.logInfo(`saving to localStorage key=${key}`);
    this._storage.setItem(key, JSON.stringify(value));
    LogService.logDebug(`saved to localStorage key=${key}, value=`, value);
  }

  public setNestedItem(fullPath: string, value: any): void {
    LogService.logInfo(`saving to localStorage key=${fullPath}`);

    const path = fullPath.split('.');
    const mainKey = path.shift();

    if (!mainKey) {
      LogService.logError('Invalid path provided.');
      return;
    }

    const itemString = localStorage.getItem(mainKey);
    let item;

    if (itemString) {
      try {
        item = JSON.parse(itemString);
      } catch (error) {
        LogService.logError('Error parsing JSON from localStorage:', error);
        return;
      }
    } else {
      item = {
        [mainKey]: {},
      };
    }

    let nestedProperty = item[mainKey];
    for (let i = 0; i < path.length; i++) {
      const key = path[i];

      if (i === path.length - 1) {
        nestedProperty[key] = value;
      } else {
        if (!nestedProperty[key] || typeof nestedProperty[key] !== 'object') {
          nestedProperty[key] = {};
        }
        nestedProperty = nestedProperty[key];
      }
    }

    localStorage.setItem(mainKey, JSON.stringify(item));
    LogService.logDebug(`saved to localStorage key=${fullPath}, value=`, value);
  }

  getItem<T>(key: string): T | undefined | null {
    LogService.logInfo(`getting from localStorage key=${key}`);
    const value = this._storage.getItem(key);
    LogService.logDebug(`got from localStorage key=${key}, value=`, value);

    return JSON.parse(value);
  }

  public getNestedItem<T>(fullPath: string): T | undefined {
    LogService.logInfo(`getting from localStorage key=${fullPath}`);

    const path = fullPath.split('.');
    const mainKey = path.shift();

    if (!mainKey) {
      LogService.logError('Invalid path provided.');
      return undefined;
    }

    const itemString = localStorage.getItem(mainKey);

    if (itemString) {
      try {
        const item = JSON.parse(itemString);

        let nestedProperty: any = item;
        for (const key of path) {
          if (
            nestedProperty &&
            typeof nestedProperty === 'object' &&
            key in nestedProperty
          ) {
            nestedProperty = nestedProperty[key];
          } else {
            return undefined;
          }
        }

        LogService.logDebug(
          `got from localStorage key=${fullPath}, value=`,
          nestedProperty
        );

        return nestedProperty as T;
      } catch (error) {
        LogService.logError('Error parsing JSON from localStorage:', error);
        return undefined;
      }
    } else {
      LogService.logInfo(`No item found in localStorage with key '${mainKey}'.`);
      return undefined;
    }
  }

  clear() {
    LogService.logInfo('clearing localStorage...');
    this._storage.clear();
  }

  removeItem(key: string) {
    LogService.logInfo(`removing from localStorage key=${key}`);
    this._storage.removeItem(key);
    LogService.logDebug(`removed from localStorage key=${key}`);
  }

  public removeNestedItem(fullPath: string): void {
    LogService.logInfo(`removing from localStorage key=${fullPath}`);
    const path = fullPath.split('.');
    const mainKey = path.shift();

    if (!mainKey) {
      LogService.logError('Invalid path provided.');
      return;
    }

    const itemString = localStorage.getItem(mainKey);
    let item;

    if (itemString) {
      try {
        item = JSON.parse(itemString);
      } catch (error) {
        LogService.logError('Error parsing JSON from localStorage:', error);
        return;
      }
    } else {
      LogService.logError(
        `No item found in localStorage with key '${mainKey}'.`
      );
      return;
    }

    let nestedProperty = item;
    for (let i = 0; i < path.length - 1; i++) {
      const key = path[i];
      if (
        nestedProperty &&
        typeof nestedProperty === 'object' &&
        key in nestedProperty
      ) {
        nestedProperty = nestedProperty[key];
      } else {
        LogService.logError(
          `Path '${path.slice(0, i + 1).join('.')}' not found.`
        );
        return;
      }
    }

    const finalKey = path[path.length - 1];
    if (
      nestedProperty &&
      typeof nestedProperty === 'object' &&
      finalKey in nestedProperty
    ) {
      delete nestedProperty[finalKey];
    } else {
      LogService.logError(`Key '${finalKey}' not found.`);
      return;
    }

    localStorage.setItem(mainKey, JSON.stringify(item));
    LogService.logDebug(`removed from localStorage key=${fullPath}`);
  }

  key(index: number): string {
    LogService.logInfo(`getting from localStorage index=${index}`);
    const key = this._storage.key(index);
    LogService.logDebug(`getting from localStorage index=${index}, key=`, key);

    return key;
  }
}
